import Moment from 'moment';
import API from 'utils/api';
import { onError } from 'utils/handlers';
import {
  ESTADO_SEGUIMIENTO,
  evaluarSeguimiento,
  huellaRegistro,
} from 'api/nominas/seguimientoProceso';

/**
 * Capa de timbrado de nómina: única puerta del frontend hacia el servicio de timbrado.
 * Las vistas no conocen URLs, códigos HTTP, descripciones de procesos ni la forma
 * de las respuestas del backend. Ninguna función lanza; los errores van a onError.
 */

// Descripciones registradas por las tasks Celery en ProcesoEnCursoTerminado (backend)
const DESCRIPCION_PROCESO_TIMBRADO = 'Timbrar Nomina';
const DESCRIPCION_PROCESO_CONSULTA = 'Consultar Timbrado Nomina';
const DESCRIPCION_PROCESO_CANCELACION = 'Cancelar Timbrado Nomina';

// Fila de bloqueo por nómina de Timbrar Todo (backend: bloqueo_timbrado_nomina.py). El
// servidor la toma antes de responder 202 y solo la tarea dueña la libera al terminar,
// después de cerrar la fila global; el seguimiento la usa para reconocer el término.
export const claveBloqueoTimbrado = (nominaId) => `bloqueo-timbrado-nomina-${nominaId}`;

const SIN_RESULTADO = {
  iniciado: false, procesoEnCurso: false, mensaje: null, proceso: null,
};

// La fila de progreso es global por descripción (no distingue nómina, usuario ni
// entidad): si ya está procesando, lanzar otra tarea la duplicaría en la cola.
const MENSAJE_PROCESO_EN_CURSO = 'Ya existe un proceso de este tipo en curso (puede ser de otra '
  + 'nómina). Espere a que termine antes de lanzarlo de nuevo.';

// Timbrar (Todo o Seleccionados) tiene su propia guarda en el servidor para cualquier proveedor
// (bloqueo por nómina con caducidad, 409). Para él la fila global no frena el lanzamiento: si
// una corrida murió sin cerrarla, quedaba en `procesando` y bloqueaba todas las nóminas sin
// vencer nunca. Cancelar Todo del servicio masivo no tiene esa guarda y la conserva.
const PROCESOS_CON_GUARDA_EN_SERVIDOR = [DESCRIPCION_PROCESO_TIMBRADO];

// El backend responde 409 cuando la nómina ya tiene un proceso pendiente o en
// ejecución (p. ej. Timbrar Todo con la tarea aún en cola). No es un error: no se
// lanzó nada y no hay corrida nueva que seguir.
const MENSAJE_CONFLICTO_POR_DEFECTO = 'Ya hay un proceso pendiente o en ejecución para esta '
  + 'nómina. Espere a que termine antes de volver a lanzarlo.';

// Banderas de SAC que apagan la cancelación (por recibo y Cancelar Todo) y la consulta de
// estado SystiCloud (tickets 50 y 53). SAC no expone su valor: con la bandera apagada responde
// 409 sin enviar nada, con un `detail` que la nombra. Es la única señal disponible.
export const BANDERA_CANCELACION = 'SYSTICLOUD_CANCELACION_HABILITADA';
export const BANDERA_CONSULTA_ESTADO = 'SYSTICLOUD_CONSULTA_ESTADO_HABILITADA';

/**
 * Bandera apagada que explica un error del servidor (409 cuyo detalle la nombra).
 * @param {Error} err error de axios
 * @returns {string|null} BANDERA_CANCELACION, BANDERA_CONSULTA_ESTADO o null si es otro error
 */
export const banderaDeshabilitada = (err) => {
  const detalle = err?.response?.status === 409 ? err.response.data?.detail : null;
  if (typeof detalle !== 'string') return null;
  return [BANDERA_CANCELACION, BANDERA_CONSULTA_ESTADO]
    .find((bandera) => new RegExp(`\\b${bandera}\\b`).test(detalle)) || null;
};

// Consultas de progreso (una cada 2 s, ≈ 11 min) antes de dejar de dar seguimiento.
// Es también el tiempo de espera para confirmar el inicio: la tarea va a la cola
// por defecto de Celery, compartida con otros procesos, y puede tardar en tomarse.
export const MAX_CONSULTAS_SEGUIMIENTO = 340;

/**
 * Lee la fila de progreso de un proceso sin avisar al usuario si falla: quien la
 * llama decide qué hacer (la consulta compartida de procesos en curso mostraría
 * un aviso en cada falla del seguimiento).
 * @param {string} descripcionProceso
 * @returns {Promise<{ok: boolean, registro: object|null}>}
 */
const leerFilaProceso = async (descripcionProceso) => {
  try {
    const response = await API.get('/catalogos/proceso-terminado/', {
      params: { descripcion: descripcionProceso },
    });
    const { data } = response;
    const lista = Array.isArray(data) ? data : (data?.results || []);
    return { ok: true, registro: lista[0] || null };
  } catch (err) {
    return { ok: false, registro: null };
  }
};

/**
 * Lee la fila de progreso del proceso antes de lanzarlo, para no confundirla
 * después con la de la corrida nueva. Si falla, el seguimiento solo acepta el
 * término tras ver el proceso en curso.
 * @param {string} descripcionProceso
 * @returns {Promise<{leido: boolean, huella: string|null, procesando: boolean}>}
 */
const leerEstadoPrevio = async (descripcionProceso) => {
  const { ok, registro } = await leerFilaProceso(descripcionProceso);
  return ok
    ? { leido: true, huella: huellaRegistro(registro), procesando: registro?.procesando === true }
    : { leido: false, huella: null, procesando: false };
};

/**
 * Interpreta la respuesta de una acción de timbrado sobre la nómina completa.
 * El backend responde 202 cuando lanzó el proceso en background (hay que dar
 * seguimiento); 200 significa que no había nada que procesar y solo regresa un
 * mensaje informativo.
 * @param {object|false} response
 * @param {string} descripcionProceso
 * @param {{leido: boolean, huella: string|null}} previo fila de progreso antes de lanzar
 * @param {string|null} bloqueo descripción de la fila de bloqueo de la nómina, si aplica
 * @returns {{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}}
 */
const interpretarInicio = (response, descripcionProceso, previo, bloqueo) => {
  if (response?.status === 202) {
    return {
      iniciado: true,
      procesoEnCurso: false,
      mensaje: null,
      proceso: bloqueo
        ? { descripcion: descripcionProceso, previo, bloqueo }
        : { descripcion: descripcionProceso, previo },
    };
  }
  if (response?.data) {
    return {
      ...SIN_RESULTADO,
      mensaje: typeof response.data === 'string'
        ? response.data
        : 'No fue necesario iniciar el proceso.',
    };
  }
  return SIN_RESULTADO;
};

/**
 * Lanza un proceso de timbrado sobre la nómina completa, salvo que la fila de
 * progreso ya esté procesando (salvo Timbrar, que lo frena el servidor). Si la
 * lectura previa falla se lanza igual: el seguimiento solo acepta el término tras
 * ver el proceso en curso. Si el backend
 * rechaza el lanzamiento con 409 (proceso pendiente o en ejecución para la nómina),
 * regresa `procesoEnCurso` con su mensaje, sin aviso de error ni seguimiento.
 * @param {string} descripcionProceso
 * @param {() => Promise<object>} lanzar petición que inicia el proceso
 * @param {string|null} [bloqueo] fila de bloqueo de la nómina que se sigue (solo Timbrar Todo)
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
const lanzarProceso = async (
  descripcionProceso,
  lanzar,
  bloqueo = null,
  rechazoComoMensaje = false,
) => {
  try {
    const previo = await leerEstadoPrevio(descripcionProceso);
    if (previo.procesando && !PROCESOS_CON_GUARDA_EN_SERVIDOR.includes(descripcionProceso)) {
      return { ...SIN_RESULTADO, procesoEnCurso: true, mensaje: MENSAJE_PROCESO_EN_CURSO };
    }
    const response = await lanzar();
    return interpretarInicio(response, descripcionProceso, previo, bloqueo);
  } catch (err) {
    const detalleRechazo = err?.response?.data?.detail;
    if (rechazoComoMensaje && err?.response?.status === 400 && typeof detalleRechazo === 'string') {
      // C3: el servidor no lanzó nada (p. ej. ningún recibo seleccionado es timbrable)
      return { ...SIN_RESULTADO, mensaje: detalleRechazo, rechazado: true };
    }
    if (err?.response?.status === 409) {
      const detalle = err.response.data?.detail;
      const bandera = banderaDeshabilitada(err);
      if (bandera) {
        // No es un proceso en curso: la operación está apagada en el servidor
        return { ...SIN_RESULTADO, mensaje: detalle, banderaDeshabilitada: bandera };
      }
      return {
        ...SIN_RESULTADO,
        procesoEnCurso: true,
        mensaje: typeof detalle === 'string' && detalle ? detalle : MENSAJE_CONFLICTO_POR_DEFECTO,
      };
    }
    onError(err);
    return SIN_RESULTADO;
  }
};

/**
 * GET Catálogo de estatus de timbrado
 * @returns {Promise<object[]>} siempre un arreglo ([] si falla)
 */
export const getEstatusTimbrado = async () => {
  try {
    const response = await API.get('nominas/tipos-de-estatus-timbrado');
    return Array.isArray(response.data) ? response.data : [];
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Nóminas del listado de Timbrado de Nóminas, paginadas: todas las de la entidad que
 * el usuario puede ver (el backend aplica entidad y limitaciones de tipo de nómina), en
 * cualquier estado: timbrar no depende de que la nómina esté Autorizada.
 * @param {number} page
 * @param {object|null} q filtros de búsqueda de la tabla
 * @returns {Promise<object|null>} respuesta paginada, null si falla
 */
export const getNominasParaTimbrado = async (page = 1, q = null) => {
  try {
    const response = await API.get('nominas/nominas', {
      params: { page, ...q },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Recibos (NominaPlaza) de una nómina, paginados, con búsqueda y filtro por estatus
 * (ticket 3). `nomina` va al final de los params para que ninguna búsqueda ni filtro
 * cambie la nómina abierta.
 * @param {number} nominaId
 * @param {number} page
 * @param {object|null} q búsqueda de la tabla: filtros de texto de NominaPlazaFilterSet
 *   (`plaza_no`, `empleado_numero`, `empleado_numero_y_nombre_completo`,
 *   `mensaje_estatus_timbrado`, ...)
 * @param {number|null} estatus id del catálogo de estatus de timbrado (filtro exacto)
 * @returns {Promise<object|null>} respuesta paginada, null si falla
 */
export const getRecibosDeNomina = async (nominaId, page = 1, q = null, estatus = null) => {
  try {
    const response = await API.get('nominas/nominas-plazas', {
      params: {
        ...q,
        ...(estatus ? { tipo_de_estatus_timbrado: estatus } : {}),
        page,
        nomina: nominaId,
      },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Datos del timbrado de un recibo (NominaPlaza).
 * Un recibo puede tener varios registros de timbrado; se regresa el más reciente
 * (mayor id) y el total de registros. No expone campos internos del servicio
 * (uuid_peticion, ruta_xml). `xml` se incluye para la descarga/vista del XML.
 * Siempre se pide filtrado por nomina_plaza: sin ese filtro el backend regresaría
 * los timbrados (con XML) de toda la entidad.
 *
 * Dependencia de backend por confirmar: hoy `xml` solo lo escribe el callback del
 * PAC (`timbrados-nomina-actualizar-timbrado`), y ningún flujo de SAC revisado
 * escribe `fecha_de_timbrado` ni `importe`. Sin ese callback el panel muestra '—'
 * en Fecha e Importe y el XML queda deshabilitado; no es un error del frontend.
 * @param {number} nominaPlazaId
 * @returns {Promise<{registro: object|null, total: number, error: boolean}>}
 */
export const getDatosTimbradoRecibo = async (nominaPlazaId) => {
  if (!nominaPlazaId) return { registro: null, total: 0, error: false };
  try {
    // Sin `page` el backend responde sin paginar (todos los registros del recibo)
    const response = await API.get('nominas/nominas-plazas-timbrados', {
      params: { nomina_plaza: nominaPlazaId },
    });
    const lista = Array.isArray(response.data) ? response.data : response.data?.results;
    const registros = (Array.isArray(lista) ? lista : [])
      .filter((r) => Number(r?.nomina_plaza) === Number(nominaPlazaId));
    const ultimo = registros.reduce((max, r) => (!max || r.id > max.id ? r : max), null);
    return {
      registro: ultimo && {
        uuid: ultimo.uuid_timbrado || null,
        fechaTimbrado: ultimo.fecha_de_timbrado || null,
        rfcReceptor: ultimo.rfc_receptor || null,
        importe: ultimo.importe ?? null,
        mensaje: ultimo.mensaje_estatus_timbrado || null,
        estatusId: ultimo.tipo_de_estatus_timbrado ?? null,
        xml: ultimo.xml || null,
      },
      total: registros.length,
      error: false,
    };
  } catch (err) {
    onError(err);
    return { registro: null, total: 0, error: true };
  }
};

// Historial de intentos SystiCloud (ticket 36). El servidor siempre pagina (T35) con un
// máximo de 200 por página; se pide esa página completa y la vista avisa si hay más.
export const TAMANO_HISTORIAL_INTENTOS = 200;

const SIN_HISTORIAL = {
  intentos: [], total: 0, error: false, sinPermiso: false,
};

/**
 * GET Historial de intentos de timbrado SystiCloud de un recibo (ticket 36, TC-3).
 * Solo lectura; el servidor entrega la descripción ya saneada y nunca XML ni RFC.
 * @param {number} nominaPlazaId
 * @returns {Promise<{intentos: object[], total: number, error: boolean, sinPermiso: boolean}>}
 */
export const getHistorialIntentosTimbrado = async (nominaPlazaId) => {
  if (!nominaPlazaId) return SIN_HISTORIAL;
  try {
    const response = await API.get('nominas/intentos-timbrado-systicloud', {
      params: { nomina_plaza: nominaPlazaId, page: 1, page_size: TAMANO_HISTORIAL_INTENTOS },
    });
    const resultados = Array.isArray(response.data?.results) ? response.data.results : [];
    const intentos = resultados
      .filter((r) => Number(r?.nomina_plaza) === Number(nominaPlazaId))
      .map((r) => ({
        id: r.id,
        numeroDeIntento: r.numero_de_intento ?? null,
        estado: r.estado || null,
        estadoDescripcion: r.estado_descripcion || r.estado || null,
        fechaEnvio: r.fecha_envio || null,
        fechaRespuesta: r.fecha_respuesta || null,
        codigo: r.codigo ?? null,
        descripcion: r.descripcion || null,
        descripcionOmitida: r.descripcion_omitida === true,
        violaciones: Array.isArray(r.violaciones) ? r.violaciones : [],
        descartadoPor: r.descartado_por || null,
        descartadoEn: r.descartado_en || null,
        esUltimoIntento: r.es_ultimo_intento === true,
      }));
    const total = Number(response.data?.count);
    return {
      intentos,
      total: Number.isInteger(total) && total >= intentos.length ? total : intentos.length,
      error: false,
      sinPermiso: false,
    };
  } catch (err) {
    // Sin view_intentotimbradosysticloud no es una falla: la vista lo explica en línea
    if (err?.response?.status === 403) return { ...SIN_HISTORIAL, sinPermiso: true };
    onError(err);
    return { ...SIN_HISTORIAL, error: true };
  }
};

/**
 * Indicador derivado del historial, sin estatus nuevo en el catálogo (TC-3).
 * Se basa en el intento que el servidor marca como último del recibo.
 * @param {object[]} intentos
 * @param {number} total
 * @returns {{tipo: 'tras_reintento'|'sigue_fallando', texto: string}|null}
 */
export const indicadorHistorialIntentos = (intentos, total) => {
  const ultimo = (intentos || []).find((i) => i.esUltimoIntento);
  if (!ultimo || !ultimo.estado || ultimo.estado === 'enviando') return null;
  if (ultimo.estado === 'timbrado') {
    return total > 1 ? { tipo: 'tras_reintento', texto: 'Timbrado tras Reintento' } : null;
  }
  return {
    tipo: 'sigue_fallando',
    texto: `Sigue Fallando (${total} ${total === 1 ? 'Intento' : 'Intentos'})`,
  };
};

/**
 * PUT Timbrado de todos los recibos de la nómina
 * @param {number} nominaId
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarTimbrado = async (nominaId, reciboIds = null) => {
  // C3: con `reciboIds` es el mismo Timbrar Todo (mismo bloqueo y 409) acotado a esos recibos.
  // El servidor descarta los que no son timbrables y revalida cada XML antes de enviarlo.
  const selectivo = Array.isArray(reciboIds);
  const url = `nominas/timbrados-nomina-timbrado-completo/${nominaId}`;
  let seleccion = null;
  const inicio = await lanzarProceso(
    DESCRIPCION_PROCESO_TIMBRADO,
    async () => {
      if (!selectivo) return API.put(url);
      const response = await API.put(url, { recibos: reciboIds });
      seleccion = {
        seleccionados: response?.data?.seleccionados ?? null,
        descartados: response?.data?.descartados ?? null,
      };
      return response;
    },
    claveBloqueoTimbrado(nominaId),
    selectivo,
  );
  return selectivo ? { ...inicio, seleccion } : inicio;
};

// Máximo de recibos por consulta de validación previa
// (SAC: validacion_previa_timbrado.MAXIMO_RECIBOS)
export const MAXIMO_RECIBOS_VALIDACION = 50;

/**
 * GET Validación previa de recibos, sin timbrar (C2/C4). Solo lectura en el servidor.
 * @param {number} nominaId
 * @param {number[]} reciboIds ids de la página visible (se recortan a MAXIMO_RECIBOS_VALIDACION)
 * @returns {Promise<{error: boolean, porRecibo: Object<number, {estado: string, timbrable: boolean,
 *   problemas: {codigo: string, elemento: string, donde: string, mensaje: string}[]}>}>}
 *   Sin aviso al usuario si falla: la columna muestra "No validado".
 */
export const validarRecibosParaTimbrado = async (nominaId, reciboIds) => {
  const ids = (reciboIds || []).slice(0, MAXIMO_RECIBOS_VALIDACION);
  if (!nominaId || !ids.length) return { error: false, porRecibo: {} };
  try {
    const response = await API.get(`nominas/timbrados-nomina-validacion-previa/${nominaId}/`, {
      params: { recibos: ids.join(',') },
    });
    const lista = Array.isArray(response?.data?.recibos) ? response.data.recibos : [];
    const porRecibo = {};
    lista.forEach((recibo) => {
      porRecibo[recibo.id] = {
        estado: recibo.estado,
        timbrable: recibo.timbrable === true,
        problemas: Array.isArray(recibo.problemas) ? recibo.problemas : [],
      };
    });
    return { error: false, porRecibo };
  } catch (err) {
    return { error: true, porRecibo: {} };
  }
};

/**
 * Campos del recibo (NominaPlaza) que se pueden corregir antes de timbrar. El XML se arma con
 * estos datos guardados en el recibo, no con el catálogo vigente del empleado.
 */
export const CAMPOS_CORREGIBLES_RECIBO = Object.freeze([
  'curp',
  'rfc',
  'numero_de_empleado',
  'numero_de_seguridad_social',
  'codigo_postal_fiscal_texto_libre',
  'clave_entidad_federativa',
  'registro_patronal',
  'tipo_de_contrato_sat',
  'tipo_regimen_nomina',
  'tipo_de_jornada_sat',
]);

/**
 * PATCH Datos corregibles de un recibo (endpoint existente nominas-plazas). El servidor aplica
 * sus protecciones: no modifica recibos Timbrados/Cancelados/En Buzón/Pendientes de Conciliar,
 * con un envío ambiguo o con un timbrado en curso, ni registros cuyo estado no permite editar.
 * `plaza`, `nomina` y el sujeto van siempre: el serializer los usa para validar duplicados.
 * @param {object} recibo registro del listado de recibos
 * @param {object} cambios solo campos de CAMPOS_CORREGIBLES_RECIBO
 * @returns {Promise<object|null>} recibo actualizado, null si el servidor lo rechazó
 */
export const guardarDatosRecibo = async (recibo, cambios) => {
  const datos = {
    plaza: recibo.plaza,
    nomina: recibo.nomina,
    empleado: recibo.empleado ?? null,
    becario: recibo.becario ?? null,
    honorario_asimilado: recibo.honorario_asimilado ?? null,
  };
  Object.keys(cambios || {}).forEach((campo) => {
    if (CAMPOS_CORREGIBLES_RECIBO.includes(campo)) datos[campo] = cambios[campo];
  });
  try {
    const response = await API.patch(`nominas/nominas-plazas/${recibo.id}/`, datos);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Consulta del estatus de timbrado de la nómina
 * @param {number} nominaId
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarConsulta = (nominaId) => lanzarProceso(
  DESCRIPCION_PROCESO_CONSULTA,
  () => API.get(`nominas/timbrados-nomina-consulta/${nominaId}`),
);

/**
 * Motivos de cancelación que ofrece la interfaz (ticket 37, TC-1), con el nombre del
 * catálogo c_MotivoCancelacion del SAT. El 01 (sustitución) queda fuera: SAC no emite
 * el CFDI sustituto. El servidor valida el motivo de nuevo.
 */
export const MOTIVO_SIN_OPERACION = '03';
export const MOTIVOS_CANCELACION = Object.freeze([
  {
    clave: '02',
    nombre: 'Comprobante emitido con errores sin relación',
    explicacion: 'El CFDI no debió emitirse, está duplicado o tiene errores, y no se sustituye '
      + 'en esta solicitud.',
  },
  {
    clave: MOTIVO_SIN_OPERACION,
    nombre: 'No se llevó a cabo la operación',
    explicacion: 'El pago de nómina que ampara el CFDI no se realizó.',
  },
]);

/**
 * POST Cancelación del timbrado de todos los recibos de la nómina, con el motivo elegido
 * @param {number} nominaId
 * @param {string} motivo clave de MOTIVOS_CANCELACION
 * @returns {Promise<{iniciado: boolean, procesoEnCurso: boolean, mensaje: string|null,
 *   proceso: object|null}>}
 */
export const iniciarCancelacionNomina = (nominaId, motivo) => lanzarProceso(
  DESCRIPCION_PROCESO_CANCELACION,
  () => API.post(`nominas/timbrados-nomina-cancelacion-completa/${nominaId}`, { motivo }),
);

// Estados de la solicitud que el SAT recibió (acuse 201/202): ninguno confirma la
// cancelación; solo la consulta de estado lo hace (ticket 31).
const SOLICITUD_RECIBIDA = 'recibida';
const SOLICITUD_YA_CANCELADO = 'ya_cancelado';
const SOLICITUD_SIN_RESPUESTA = 'sin_respuesta';

/**
 * Una solicitud recibida o previamente cancelada según el SAT sigue pendiente de
 * confirmar: nunca se presenta como cancelación confirmada (tickets 31 y 38).
 * @param {string} estado
 * @returns {boolean}
 */
export const esSolicitudPendiente = (estado) => estado === SOLICITUD_RECIBIDA
  || estado === SOLICITUD_YA_CANCELADO;

// Estado del CFDI ante el SAT que confirma la cancelación, con la misma regla que SAC
// (`ResultadoConsultaEstado.cancelado`): sin distinguir mayúsculas.
const esEstadoSatCancelado = (estadoSat) => String(estadoSat || '').trim().toLowerCase() === 'cancelado';

// Resultado de una consulta de estado que SystiCloud sí obtuvo del SAT
const RESULTADO_CONSULTA_OBTENIDO = 'obtenido';

/**
 * Cómo se presenta una solicitud de cancelación (ticket 38). El estado de la solicitud
 * no cambia cuando el SAT confirma la cancelación: SAC solo guarda en ella la última
 * consulta de estado. Por eso una solicitud pendiente cuya última consulta guardada dice
 * `Cancelado` se presenta como confirmada, y nunca al revés.
 * @param {{estado: string, estadoSat: string}} solicitud
 * @returns {'confirmada'|'pendiente'|null}
 */
export const presentacionSolicitudCancelacion = (solicitud) => {
  if (!esSolicitudPendiente(solicitud?.estado)) return null;
  return esEstadoSatCancelado(solicitud?.estadoSat) ? 'confirmada' : 'pendiente';
};

/**
 * Una consulta de estado confirma la cancelación del CFDI cuando se obtuvo del SAT y su
 * estado es `Cancelado`, haya cambiado o no el recibo (ticket 38).
 * @param {{resultado: string, estadoSat: string}} consulta
 * @returns {boolean}
 */
export const consultaConfirmaCancelacion = (consulta) => (
  consulta?.resultado === RESULTADO_CONSULTA_OBTENIDO && esEstadoSatCancelado(consulta?.estadoSat)
);

// Historial de cancelaciones (ticket 38, TC-2). El servidor siempre pagina (T35) con un
// máximo de 200 por página; se pide esa página completa y la vista avisa si hay más.
export const TAMANO_HISTORIAL_CANCELACIONES = 200;

const SIN_REGISTROS = {
  registros: [], total: 0, error: false, sinPermiso: false,
};

// GET paginado de un historial de cancelación del recibo, filtrado de nuevo por recibo
const getHistorialCancelacion = async (ruta, nominaPlazaId, mapear) => {
  if (!nominaPlazaId) return SIN_REGISTROS;
  try {
    const response = await API.get(ruta, {
      params: { nomina_plaza: nominaPlazaId, page: 1, page_size: TAMANO_HISTORIAL_CANCELACIONES },
    });
    const resultados = Array.isArray(response.data?.results) ? response.data.results : [];
    const registros = resultados
      .filter((r) => Number(r?.nomina_plaza) === Number(nominaPlazaId))
      .map(mapear);
    const total = Number(response.data?.count);
    return {
      registros,
      total: Number.isInteger(total) && total >= registros.length ? total : registros.length,
      error: false,
      sinPermiso: false,
    };
  } catch (err) {
    // Sin el permiso view_* no es una falla: la vista lo explica en línea
    if (err?.response?.status === 403) return { ...SIN_REGISTROS, sinPermiso: true };
    onError(err);
    return { ...SIN_REGISTROS, error: true };
  }
};

/**
 * GET Solicitudes de cancelación SystiCloud de un recibo (ticket 38, TC-2). Solo lectura;
 * el listado nunca trae el contenido del acuse.
 * @param {number} nominaPlazaId
 * @returns {Promise<{registros: object[], total: number, error: boolean, sinPermiso: boolean}>}
 */
export const getSolicitudesCancelacion = (nominaPlazaId) => getHistorialCancelacion(
  'nominas/cancelaciones-systicloud',
  nominaPlazaId,
  (r) => ({
    id: r.id,
    uuid: r.uuid || null,
    motivo: r.motivo || null,
    folioSustitucion: r.folio_sustitucion || null,
    estado: r.estado || null,
    estadoDescripcion: r.estado_descripcion || r.estado || null,
    fechaSolicitud: r.fecha_solicitud || null,
    fechaRespuesta: r.fecha_respuesta || null,
    codigo: r.codigo ?? null,
    estatusUuid: r.estatus_uuid || null,
    descripcion: r.descripcion || null,
    estadoSat: r.estado_sat || null,
    esCancelable: r.es_cancelable || null,
    estatusCancelacion: r.estatus_cancelacion || null,
    fechaConsultaEstado: r.fecha_consulta_estado || null,
    tieneAcuse: r.tiene_acuse === true,
    usuario: r.usuario_creo_nombre || null,
  }),
);

/**
 * GET Consultas de estado de cancelación ante el SAT de un recibo (ticket 38, TC-2).
 * @param {number} nominaPlazaId
 * @returns {Promise<{registros: object[], total: number, error: boolean, sinPermiso: boolean}>}
 */
export const getConsultasEstadoCancelacion = (nominaPlazaId) => getHistorialCancelacion(
  'nominas/consultas-estado-cancelacion-systicloud',
  nominaPlazaId,
  (r) => ({
    id: r.id,
    fechaConsulta: r.fecha_consulta || null,
    resultado: r.resultado || null,
    estadoSat: r.estado_sat || null,
    esCancelable: r.es_cancelable || null,
    estatusCancelacion: r.estatus_cancelacion || null,
    codigo: r.codigo ?? null,
    descripcion: r.descripcion || null,
    reciboActualizado: r.recibo_actualizado === true,
    usuario: r.usuario_creo_nombre || null,
  }),
);

/**
 * Nombre del archivo del acuse con la misma regla del servidor: el UUID del CFDI sin
 * caracteres fuera de A-Z, a-z, 0-9 y guion; el id de la solicitud si queda vacío.
 * @param {string} uuid
 * @param {number} solicitudId
 * @returns {string}
 */
export const nombreArchivoAcuse = (uuid, solicitudId) => {
  const limpio = String(uuid || '').replace(/[^A-Za-z0-9-]/g, '');
  return `acuse-cancelacion-${limpio || solicitudId}.xml`;
};

// `detail` de una respuesta de error pedida como Blob
const detalleDeError = async (data) => {
  if (data && typeof data.text === 'function') {
    try {
      return JSON.parse(await data.text())?.detail || null;
    } catch {
      return null;
    }
  }
  return data?.detail || null;
};

/**
 * GET Acuse de una solicitud de cancelación, verificado por el servidor con su sha256.
 * El contenido se entrega tal cual llega, sin transformarlo.
 * @param {number} solicitudId
 * @param {AbortSignal} [signal] cancela la petición si la vista se cierra antes de terminar
 * @returns {Promise<{acuse: Blob|null, detalle: string|null, cancelada?: boolean}>} `detalle`
 *   trae el mensaje del servidor cuando no hay acuse (404) o no es verificable (409);
 *   `cancelada` indica que la vista abortó la petición
 */
export const descargarAcuseCancelacion = async (solicitudId, signal) => {
  try {
    const response = await API.get(`nominas/cancelaciones-systicloud/${solicitudId}/acuse`, {
      responseType: 'blob',
      signal,
    });
    return { acuse: response.data, detalle: null };
  } catch (err) {
    // Cancelada por la vista: no es un error ni debe avisarse
    if (err?.code === 'ERR_CANCELED' || signal?.aborted) {
      return { acuse: null, detalle: null, cancelada: true };
    }
    const estatus = err?.response?.status;
    if (estatus === 404 || estatus === 409) {
      return {
        acuse: null,
        detalle: (await detalleDeError(err.response.data))
          || 'El acuse de esta solicitud no está disponible.',
      };
    }
    onError(err);
    return { acuse: null, detalle: null };
  }
};

/**
 * Resultado de una operación que el servidor rechazó por tener su bandera apagada (409).
 * @param {Error} err error de axios
 * @returns {{banderaDeshabilitada: string, mensaje: string}|null}
 */
const operacionDeshabilitada = (err) => {
  const bandera = banderaDeshabilitada(err);
  return bandera ? { banderaDeshabilitada: bandera, mensaje: err.response.data.detail } : null;
};

/**
 * POST Cancelación SystiCloud de un recibo (NominaPlaza), en la misma petición.
 * @param {number} nominaPlazaId
 * @param {string} motivo clave de MOTIVOS_CANCELACION
 * @returns {Promise<object|null>} respuesta del servidor (`estado`, `codigo`, `estatus_uuid`,
 *   `tipo_de_estatus_timbrado`, `mensaje`, `solicitud_pendiente`); con la bandera apagada,
 *   `{banderaDeshabilitada, mensaje}` sin onError; null si falló por otra causa
 */
export const cancelarRecibo = async (nominaPlazaId, motivo) => {
  try {
    const response = await API.post(`nominas/timbrados-nominaplaza-cancelacion/${nominaPlazaId}`, { motivo });
    return response?.data || null;
  } catch (err) {
    const deshabilitada = operacionDeshabilitada(err);
    if (deshabilitada) return deshabilitada;
    onError(err);
  }
  return null;
};

/**
 * Aviso para la persona según el resultado de la solicitud de cancelación de un recibo.
 * Un acuse recibido nunca se presenta como cancelación terminada.
 * @param {object} resultado respuesta de cancelarRecibo
 * @returns {{tipo: string, titulo: string, descripcion: string}}
 */
export const interpretarSolicitudCancelacion = (resultado) => {
  if (resultado?.estado === SOLICITUD_RECIBIDA) {
    return {
      tipo: 'info',
      titulo: 'Solicitud de Cancelación Recibida',
      descripcion: 'El SAT recibió la solicitud (201) y el recibo sigue Timbrado. Falta '
        + 'confirmarla con «Consultar Estado».',
    };
  }
  if (resultado?.estado === SOLICITUD_YA_CANCELADO) {
    return {
      tipo: 'info',
      titulo: 'Solicitud de Cancelación Recibida',
      descripcion: 'El SAT respondió que el CFDI tenía una cancelación previa (202) y el recibo '
        + 'sigue Timbrado. Falta confirmarla con «Consultar Estado» y revisar si existe un CFDI '
        + 'sustituto o relacionado.',
    };
  }
  // Sin respuesta concluyente la solicitud pudo haberse procesado: no se afirma que falló
  if (resultado?.estado === SOLICITUD_SIN_RESPUESTA) {
    return {
      tipo: 'warning',
      titulo: 'Cancelación Sin Respuesta Concluyente',
      descripcion: resultado?.mensaje || 'La solicitud pudo haberse procesado. Use «Consultar '
        + 'Estado» antes de repetirla.',
    };
  }
  return {
    tipo: 'warning',
    titulo: 'La Cancelación No se Realizó',
    descripcion: resultado?.mensaje || 'No se pudo enviar la solicitud de cancelación.',
  };
};

/**
 * POST Consulta ante el SAT (vía SystiCloud) del estado del CFDI de un recibo. El servidor
 * pasa el recibo a Cancelado solo si el SAT lo reporta así, y registra cada consulta.
 * @param {number} nominaPlazaId
 * @returns {Promise<object|null>} respuesta del servidor; con la bandera apagada,
 *   `{banderaDeshabilitada, mensaje}` sin onError; null si falló por otra causa
 */
export const consultarEstadoCancelacion = async (nominaPlazaId) => {
  try {
    const response = await API.post(`nominas/timbrados-nominaplaza-estado-cancelacion/${nominaPlazaId}`);
    return response?.data || null;
  } catch (err) {
    const deshabilitada = operacionDeshabilitada(err);
    if (deshabilitada) return deshabilitada;
    onError(err);
  }
  return null;
};

/**
 * GET Si las banderas de SAC habilitan la cancelación (por recibo y Cancelar Todo) y la
 * consulta de estado (ticket 53), para no ofrecer la acción antes del clic. El servidor solo
 * regresa las operaciones que el usuario puede hacer; la que no regresa queda en null.
 * Sin onError: con un error, la vista deshabilita las acciones y explica el motivo.
 * @returns {Promise<{cancelacion: boolean|null, consultaEstado: boolean|null, error: boolean}>}
 */
export const getOperacionesHabilitadas = async () => {
  try {
    const response = await API.get('nominas/timbrados-systicloud-operaciones-habilitadas');
    const data = response?.data || {};
    return {
      cancelacion: typeof data.cancelacion === 'boolean' ? data.cancelacion : null,
      consultaEstado: typeof data.consulta_estado === 'boolean' ? data.consulta_estado : null,
      error: false,
    };
  } catch {
    return { cancelacion: null, consultaEstado: null, error: true };
  }
};

const SI_NO = (valor) => {
  if (valor === true) return 'Sí';
  if (valor === false) return 'No';
  return 'Sin dato';
};

/**
 * Renglones que describen el resultado de la consulta de estado de un recibo.
 * @param {object} resultado respuesta de consultarEstadoCancelacion
 * @param {number} estatusCancelado id del estatus de timbrado Cancelado
 * @returns {{tipo: string, renglones: string[], aviso: string|null}}
 */
export const describirConsultaEstado = (resultado, estatusCancelado) => {
  const pasoACancelado = resultado?.recibo_actualizado === true
    && resultado?.tipo_de_estatus_timbrado === estatusCancelado;
  const consultaFallida = resultado?.consulta && resultado.consulta !== 'obtenido';
  const renglones = [
    `Estado ante el SAT: ${resultado?.estado_sat || 'Sin dato'}`,
    `Es cancelable: ${resultado?.es_cancelable || 'Sin dato'}`,
    `Estatus de la cancelación: ${resultado?.estatus_cancelacion || 'Sin dato'}`,
    pasoACancelado
      ? 'El recibo pasó a Cancelado.'
      : 'El recibo conserva su estatus.',
    `Solicitud pendiente de confirmar: ${SI_NO(resultado?.solicitud_pendiente)}`,
  ];
  if (consultaFallida) {
    renglones.unshift('El SAT no dio una respuesta concluyente; intente la consulta más tarde.');
  }
  let tipo = 'info';
  if (pasoACancelado) tipo = 'success';
  if (consultaFallida) tipo = 'warning';
  return { tipo, renglones, aviso: resultado?.aviso || null };
};

/**
 * Si la fecha (la que el CFDI lleva como FechaPago: la final de la nómina) es de un
 * ejercicio anterior al de `hoy`. Solo alimenta un aviso informativo; nunca bloquea.
 * @param {object|string|null} fecha Moment o texto `YYYY-MM-DD` / `DD/MM/YYYY`
 * @param {Date} [hoy]
 * @returns {boolean}
 */
export const esEjercicioAnterior = (fecha, hoy = new Date()) => {
  if (!fecha) return false;
  const momento = Moment.isMoment(fecha) ? fecha : Moment(fecha, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return momento.isValid() && momento.year() < hoy.getFullYear();
};

/**
 * POST Liberación manual del bloqueo de Timbrar Todo de una nómina atascada (ticket 18).
 * El servidor rechaza (409) si no hay bloqueo o si la corrida registró actividad reciente.
 * @param {number} nominaId
 * @param {string} motivo
 * @returns {Promise<{liberado: boolean, mensaje: string|null}>}
 */
export const liberarBloqueoTimbrado = async (nominaId, motivo) => {
  try {
    const response = await API.post(`nominas/timbrados-nomina-liberar-bloqueo/${nominaId}/`, { motivo });
    return { liberado: true, mensaje: response?.data?.detail || null };
  } catch (err) {
    onError(err);
  }
  return { liberado: false, mensaje: null };
};

/**
 * GET Progreso de un proceso de timbrado lanzado con iniciar*.
 * Mientras no se confirme que la fila es de esta corrida, no regresa su avance
 * ni sus errores (serían los de la corrida anterior).
 * Si la consulta falla regresa `error: true` sin avisar al usuario; en ese caso
 * `enCurso` y `total` no son avance real y no deben mostrarse, y `seguimiento`
 * conserva lo ya confirmado.
 * Con la fila de bloqueo de Timbrar Todo, `terminado` llega con `sinResultado: true`
 * cuando la corrida terminó pero la fila global no prueba que su resultado sea suyo.
 * @param {object} proceso referencia opaca regresada por iniciar*
 * @param {object} [opciones]
 * @param {object|null} [opciones.seguimiento] el `seguimiento` de la consulta anterior
 * @param {boolean} [opciones.vencido] si esta es la última consulta permitida
 * @returns {Promise<{error: boolean, terminado: boolean, sinResultado: boolean,
 *   inicioNoConfirmado: boolean, enCurso: number, total: number, errores: string[],
 *   seguimiento: object}>}
 */
export const getProgresoProceso = async (proceso, { seguimiento = null, vencido = false } = {}) => {
  const vistoEnCurso = !!seguimiento?.vistoEnCurso;
  // El bloqueo se lee ANTES que la fila global: la tarea cierra la fila global y luego
  // libera el bloqueo, así que "bloqueo libre" implica que la fila global leída después
  // ya trae el cierre. Si no se puede leer, no es error de consulta: se usa la huella.
  let bloqueo = null;
  if (proceso?.bloqueo) {
    const lectura = await leerFilaProceso(proceso.bloqueo);
    bloqueo = {
      leido: lectura.ok,
      existe: !!lectura.registro,
      procesando: lectura.registro?.procesando === true,
    };
  }
  const { ok, registro } = await leerFilaProceso(proceso?.descripcion);
  if (!ok) {
    return {
      error: true,
      terminado: false,
      inicioNoConfirmado: vencido && !vistoEnCurso,
      enCurso: 0,
      total: 0,
      errores: [],
      seguimiento: { vistoEnCurso },
    };
  }
  const estado = evaluarSeguimiento({
    previo: proceso?.previo, registro, vistoEnCurso, vencido, bloqueo,
  });
  // Sin resultado atribuible (ticket 30): termina, pero nunca presenta el éxito ni los
  // errores de la fila global, que pueden ser de una corrida anterior.
  const sinResultado = estado === ESTADO_SEGUIMIENTO.TERMINADO_SIN_RESULTADO;
  const terminado = estado === ESTADO_SEGUIMIENTO.TERMINADO;
  const esDeEstaCorrida = terminado || estado === ESTADO_SEGUIMIENTO.EN_CURSO;

  let resultado = null;
  if (terminado) {
    try {
      resultado = registro.resultado ? JSON.parse(registro.resultado) : null;
    } catch (e) { /* resultado no es JSON */ }
  }

  return {
    error: false,
    terminado: terminado || sinResultado,
    sinResultado,
    inicioNoConfirmado: estado === ESTADO_SEGUIMIENTO.NO_CONFIRMADO,
    enCurso: esDeEstaCorrida ? registro.registro_en_curso || 0 : 0,
    total: esDeEstaCorrida ? registro.total_registros || 0 : 0,
    errores: Array.isArray(resultado?.errores) ? resultado.errores : [],
    seguimiento: { vistoEnCurso: vistoEnCurso || estado === ESTADO_SEGUIMIENTO.EN_CURSO },
  };
};

/**
 * Indica si la nómina tiene una consulta de timbrado pendiente con el servicio.
 * @param {object} nomina encabezado de la nómina
 * @returns {boolean}
 */
export const hayConsultaPendiente = (nomina) => !nomina?.nomina_termino_servicio_consulta
  && nomina?.peticion_pendiente_id != null;
